import { useState, useEffect, useRef, useContext } from "react";
import { CircularProgress } from '@mui/material';
import LanguageContext from "../../Store/LanguageContext";
import AdditionalQuestionContext from "../../Store/AdditionalQuestionContext";
import DesignationContext from "../../Store/DesignationContext";
import greentick from "../../Assets/Images/greentick.svg";
import { translate } from "../../Components/translate/translate";
import blackCross from '../../Assets/Images/black-cross.svg'
import useWindowDimensions from "../../Hooks/useWindowDimensions";
import { preventWidow } from "../../utils/widowFix";

const EmailResultsModal = ({ isModalOpen, onClose, hashId }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    terms: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);
  const [isCloseHovered, setIsCloseHovered] = useState(false);

  const langCtx = useContext(LanguageContext);
  const addCtx = useContext(AdditionalQuestionContext);
  const designationCtx = useContext(DesignationContext);
  const modalRef = useRef();
  const { windowWidth } = useWindowDimensions();
  const isMobile = windowWidth <= 768;
  
  // Debug logging
  console.log('Window width:', windowWidth, 'isMobile:', isMobile);

  // Disable background scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  // Re-translate error messages when language changes
  useEffect(() => {
    if (Object.values(errors).some(error => error !== "")) {
      const newErrors = { ...errors };
      
      // Re-translate existing error messages
      if (errors.firstName) {
        newErrors.firstName = validateName(formData.firstName, 'firstName');
      }
      if (errors.lastName) {
        newErrors.lastName = validateName(formData.lastName, 'lastName');
      }
      if (errors.email) {
        newErrors.email = validateEmail(formData.email);
      }
      if (errors.terms) {
        newErrors.terms = handleTranslate("Please agree to the Terms and Privacy to continue.", "terms-and-privacy-email-error");
      }
      
      setErrors(newErrors);
    }
  }, [langCtx.language]);

  const handleTranslate = (text, type) => {
    switch (langCtx.language) {
      case "Nederlands": {
        return preventWidow(translate(text, "resultPageDutch", type));
      }
      default: {
        return preventWidow(text);
      }
    }
  };

  // Close modal when clicking outside
  const handleOverlayClick = (e) => {
    setIsSuccess(false);
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  // Validation functions
  const validateName = (name, fieldType) => {
    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!name.trim()) {
      return fieldType === 'firstName' ? handleTranslate("Please enter your first name.", "first-name-error") : handleTranslate("Please enter your last name.", "last-name-error");
    }
    if (!nameRegex.test(name)) {
      return fieldType === 'firstName' ? handleTranslate("Please enter your first name.", "first-name-error") : handleTranslate("Please enter your last name.", "last-name-error");
    }
    return "";
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      return handleTranslate("Please enter a valid email address.", "email-name-error");
    }
    if (!emailRegex.test(email)) {
      return handleTranslate("Please enter a valid email address.", "email-name-error");
    }
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleInputFocus = (fieldName) => {
    // Clear error when user focuses on the field
    if (errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  const handleCheckboxChange = (e) => {
    setAgreeToTerms(e.target.checked);
    // Clear terms error when user checks the checkbox
    if (errors.terms) {
      setErrors((prev) => ({ ...prev, terms: "" }));
    }
  };

  const buildPayload = () => {
    const basePayload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      fullName: formData.firstName && formData.lastName ? `${formData.firstName} ${formData.lastName}` : undefined,
      email: formData.email,
      designation: designationCtx?.designation,
      userDetails: [
        { field: "Email", value: formData.email },
        { field: "first_name", value: formData.firstName },
        { field: "last_name", value: formData.lastName },
        { field: "Diocese", value: addCtx?.diocese },
        { field: "Parish", value: addCtx?.parish },
        { field: "surveyResult", value: process.env.REACT_APP_API_URI === 'https://api.bleat.church'? `https://app.missionalwaypoint.org/results/${hashId}`: `https://d18zxghianf2es.cloudfront.net/results/${hashId}`},
        { field: "consentStatus", value: agreeToTerms ? "Agreed" : "Disagree" },
        { field: "languagePreference", value: langCtx?.language }
      ]

    };

    // remove keys with falsy values (undefined, null, "", false)
    const cleanObject = (obj) =>
      Object.fromEntries(
        Object.entries(obj)
          .filter(([_, v]) => v !== undefined && v !== null && v !== "")
          .map(([k, v]) => [k, typeof v === "object" && !Array.isArray(v) ? cleanObject(v) : v])
      );

    return cleanObject(basePayload);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const firstNameError = validateName(formData.firstName, 'firstName');
    const lastNameError = validateName(formData.lastName, 'lastName');
    const emailError = validateEmail(formData.email);
    const termsError = !agreeToTerms ? handleTranslate("Please agree to the Terms and Privacy to continue.", "terms-and-privacy-email-error") : "";

    const newErrors = {
      firstName: firstNameError,
      lastName: lastNameError,
      email: emailError,
      terms: termsError,
    };

    setErrors(newErrors);

    // Check if there are any validation errors
    const hasErrors = Object.values(newErrors).some(error => error !== "");
    
    if (hasErrors) {
      return; // Don't proceed with API call if there are validation errors
    }

    setIsLoading(true);

    try {
      // First call the user assessment API
      const url = `${process.env.REACT_APP_API_URI}/v6/user/assessment/${hashId}/user`;
      const payload = buildPayload();

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const data = await response.json();
      console.log("✅ User updated successfully:", data);

      // Then call the generate PDF API
      const pdfUrl = `${process.env.REACT_APP_API_URI}/v5/generate-pdf/${hashId}`;
      const pdfResponse = await fetch(pdfUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!pdfResponse.ok) {
        throw new Error(`PDF generation failed: ${pdfResponse.status}`);
      }

      const pdfData = await pdfResponse.json();
      console.log("✅ PDF generated successfully:", pdfData);

      // Finally call the acknowledgement API
      const languageId = langCtx?.language === "Nederlands" ? "122" : "116";
      const acknowledgementUrl = `${process.env.REACT_APP_API_URI}/v4/user/assessment/${languageId}/acknowledgement/${formData.email}`;
      
      const acknowledgementResponse = await fetch(acknowledgementUrl, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hash: hashId }),
      });

      if (!acknowledgementResponse.ok) {
        throw new Error(`Acknowledgement API failed: ${acknowledgementResponse.status}`);
      }

      const acknowledgementData = await acknowledgementResponse.json();
      console.log("✅ Acknowledgement sent successfully:", acknowledgementData);
      
      // Show success state instead of closing modal
      setIsSuccess(true);
    } catch (error) {
      console.error("❌ Error updating user:", error.message);
      alert("Failed to send results. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };


  if (!isModalOpen) return null;

  // Success state UI
  if (isSuccess) {
    return (
      <div className="overlay-emailpopup" onClick={handleOverlayClick}>
        <div className="success-modal" ref={modalRef}>
          <div className="success-icon">
            <img src={greentick} alt="greentick" />
          </div>
          <h2 className="success-title">{handleTranslate("Your email has been sent successfully.", "your-email-has-been-sent-successfully")}</h2>
          <button 
            className="close-success-button" 
            style={{ background: isCloseHovered ? '#97A97C' : 'rgba(132, 22, 29, 1)' }} 
            onClick={onClose}
            onMouseEnter={() => setIsCloseHovered(true)}
            onMouseLeave={() => setIsCloseHovered(false)}
          >
            {handleTranslate("Close", "close-cta")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overlay-emailpopup" onClick={handleOverlayClick}>
      <div className="modal" ref={modalRef}>
        <button className="close-button" onClick={onClose}> 
          <img src={blackCross} alt="blackCross" />
        </button>

        <h2 className="title">{handleTranslate("Email Results", "email-result-title")}</h2>

        <form onSubmit={handleSubmit} className="form">
          <div className="input-group">
            <label className="label">
              {handleTranslate("First Name", "first-name")}
              <span className="asterisk">*</span>
            </label>
            <input 
              type="text" 
              name="firstName" 
              placeholder="E.g. Anna" 
              value={formData.firstName} 
              onChange={handleInputChange}
              onFocus={() => handleInputFocus('firstName')} 
              className={`input ${errors.firstName ? 'input--error' : ''}`}
            />
            <div className="div-error-text">
              {errors.firstName && <span className="error-text">{errors.firstName}</span>}
            </div>
          </div>

          <div className="input-group">
            <label className="label">
              {handleTranslate("Last Name", "last-name")}
              <span className="asterisk">*</span>
            </label>
            <input 
              type="text" 
              name="lastName" 
              placeholder="E.g. Parker" 
              value={formData.lastName} 
              onChange={handleInputChange}
              onFocus={() => handleInputFocus('lastName')} 
              className={`input ${errors.lastName ? 'input--error' : ''}`}
            />
            <div className="div-error-text">
              {errors.lastName && <span className="error-text">{errors.lastName}</span>}
            </div>
          </div>

          <div className="input-group">
            <label className="label">
              {handleTranslate("Email", "email-id")}
              <span className="asterisk">*</span>
            </label>
            <input 
              type="email" 
              name="email" 
              placeholder="E.g. annapark@gmail.com" 
              value={formData.email} 
              onChange={handleInputChange}
              onFocus={() => handleInputFocus('email')} 
              className={`input ${errors.email ? 'input--error' : ''}`}
            />
            <div className="div-error-text">
              {errors.email && <span className="error-text">{errors.email}</span>}
            </div>
          </div>

          <button 
            type="submit" 
            className="submit-button"
            style={{ background: isSubmitHovered ? '#97A97C' : 'rgba(132, 22, 29, 1)'}} 
            disabled={isLoading} 
            onMouseEnter={() => setIsSubmitHovered(true)} 
            onMouseLeave={() => setIsSubmitHovered(false)}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: '#ffffff' }} />
            ) : (
              handleTranslate("Send Email", "send-email")
            )}
          </button>
          
          <div>
            <div className="checkbox-container">
              <input 
                type="checkbox" 
                id="agreeTerms" 
                checked={agreeToTerms} 
                onChange={handleCheckboxChange} 
                className="checkbox" 
              />
              <label htmlFor="agreeTerms" className="checkbox-label">
                <span className="agree-text">
                  {handleTranslate("I agree to the ", "i-send-emailterms-and-privacy-email")}
                  <a 
                    href="https://missionalwaypoint.org/privacy-policy/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="terms-link"
                  > 
                    {handleTranslate("Terms and Privacy", "terms-and-privacy-email")}
                  </a>
                </span>
              </label>
            </div>
            <div className="div-error-text">
              {errors.terms && (
                <span 
                  className="error-text" 
                  style={{ textAlign: 'center', marginTop: 10, fontSize: '14px' }}
                >
                  {errors.terms}
                </span>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailResultsModal;
